// Write detected/edited field rects into a real AcroForm using pdf-lib.
//
// pdf-lib's `addToPage` does its own rect math: it inflates the box by
// `borderWidth` and pre-rotates it for the widget's appearance rotation. Rather
// than invert that (and stay hostage to it across versions) we let it create the
// widget, then overwrite `/Rect` with the exact user-space box we computed and
// mark the field dirty so appearance streams are regenerated on save.

import {
  PDFDocument,
  PDFName,
  PDFHexString,
  PDFBool,
  StandardFonts,
  degrees,
  rgb,
} from '@cantoo/pdf-lib';

export class UnsupportedPdfError extends Error {}

/**
 * @param {ArrayBuffer|Uint8Array} bytes original PDF
 * @param {Array<{pageIndex:number, fields:Array}>} pages field placements
 * @param {object} opts
 * @returns {Promise<Uint8Array>} the fillable PDF
 */
export async function buildFillablePdf(bytes, pages, opts = {}) {
  const { flatten = false, borderWidth = 1, showBorders = true } = opts;

  let doc;
  try {
    doc = await PDFDocument.load(bytes, {
      ignoreEncryption: true,
      updateMetadata: false,
    });
  } catch (err) {
    throw new UnsupportedPdfError(
      `This PDF could not be opened for editing (${err.message}).`,
    );
  }

  assertNotXfa(doc);

  const form = doc.getForm();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const docPages = doc.getPages();
  const usedNames = new Set(form.getFields().map((f) => f.getName()));

  let created = 0;
  for (const { pageIndex, fields } of pages) {
    const page = docPages[pageIndex];
    if (!page) continue;
    const rotation = normalizeRotation(page.getRotation().angle);

    for (const field of fields) {
      const rect = sanitizeRect(field.rect);
      if (!rect) continue;
      const name = uniqueName(field.name, usedNames);

      const common = {
        x: 0,
        y: 0,
        width: 10,
        height: 10,
        rotate: degrees(rotation),
        borderWidth: showBorders ? borderWidth : 0,
        borderColor: showBorders ? rgb(0.35, 0.35, 0.45) : undefined,
        backgroundColor: undefined,
      };

      let created$;
      if (field.type === 'checkbox') {
        created$ = form.createCheckBox(name);
        created$.addToPage(page, common);
      } else {
        const tf = form.createTextField(name);
        if (field.multiline) tf.enableMultiline();
        created$ = tf;
        // addToPage seeds the /DA entry, so font sizing has to follow it.
        created$.addToPage(page, { ...common, font });
        tf.setFontSize(field.fontSize || 0); // 0 = auto-size to the box
      }

      // Replace pdf-lib's computed rect with the exact user-space box.
      const widgets = created$.acroField.getWidgets();
      const widget = widgets[widgets.length - 1];
      widget.setRectangle(rect);

      if (field.label) {
        // Hex, not a literal string: PDFString.of emits "(value)" verbatim with
        // no escaping, so a label containing a bracket or backslash — "(Pick 2,
        // in addition to your pouch)" — writes unbalanced syntax and corrupts
        // the document. Hex also carries non-ASCII labels safely.
        created$.acroField.dict.set(PDFName.of('TU'), PDFHexString.fromText(field.label));
      }
      form.markFieldAsDirty(created$.ref);
      created += 1;
    }
  }

  if (created === 0) {
    throw new UnsupportedPdfError('No fields were placed, so nothing was written.');
  }

  // Ask viewers to regenerate appearances too; some are pickier than others
  // about streams written by third-party tools.
  form.acroForm.dict.set(PDFName.of('NeedAppearances'), PDFBool.True);

  if (flatten) {
    form.flatten();
  }

  return doc.save({ updateFieldAppearances: true });
}

function assertNotXfa(doc) {
  const acroForm = doc.catalog.lookupMaybe(PDFName.of('AcroForm'), Object);
  if (acroForm && acroForm.get && acroForm.get(PDFName.of('XFA'))) {
    throw new UnsupportedPdfError(
      'This is an XFA (LiveCycle) form. Those use a separate form model that ' +
        'cannot be extended this way — it needs to be re-created as a standard PDF form.',
    );
  }
}

function normalizeRotation(angle) {
  const r = ((Math.round((angle || 0) / 90) * 90) % 360 + 360) % 360;
  return r;
}

function sanitizeRect(rect) {
  if (!rect) return null;
  const x = Number(rect.x);
  const y = Number(rect.y);
  const width = Number(rect.width);
  const height = Number(rect.height);
  if (![x, y, width, height].every(Number.isFinite)) return null;
  if (width < 2 || height < 2) return null;
  return { x, y, width, height };
}

function uniqueName(base, used) {
  const clean = (base || 'field').replace(/[.[\]]/g, '_');
  let name = clean;
  let i = 2;
  while (used.has(name)) name = `${clean}_${i++}`;
  used.add(name);
  return name;
}

/** Does this PDF already carry form fields? */
export async function inspectPdf(bytes) {
  const doc = await PDFDocument.load(bytes, {
    ignoreEncryption: true,
    updateMetadata: false,
  });
  const fields = doc.getForm().getFields();
  return { pageCount: doc.getPageCount(), existingFieldCount: fields.length };
}
